import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  UploadCloud,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
  X,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'

interface ResultadoFila {
  row: number
  message: string
}

interface ResultadoCarga {
  total_filas: number
  creadas: number
  actualizadas: number
  errores: ResultadoFila[]
}

const COLUMNS: Array<{ key: string; example: string }> = [
  { key: 'sku', example: 'BR-12345' },
  { key: 'name', example: 'Balata delantera Versa 2018' },
  { key: 'description', example: 'Juego de pastillas cerámicas premium' },
  { key: 'price', example: '850' },
  { key: 'mayoreo', example: '780' },
  { key: 'distribuidor', example: '720' },
  { key: 'especial', example: '690' },
  { key: 'sale_price', example: '699' },
  { key: 'en_oferta', example: '1' },
  { key: 'iva', example: '16' },
  { key: 'stock', example: '12' },
  { key: 'category_slug', example: 'frenos' },
  { key: 'brand_slug', example: 'bosch' },
  { key: 'weight_kg', example: '1.20' },
  { key: 'image', example: '/img/products/br-12345.jpg' },
]

/**
 * Genera una plantilla .xlsx vacía descargable sin dependencias extra del lado del cliente.
 * Usa el endpoint público que devuelve un CSV con BOM (Excel-friendly).
 * Como fallback portable, generamos el archivo en el cliente con un blob CSV.
 */
function downloadTemplate() {
  const headers = COLUMNS.map((c) => c.key).join(',')
  const sample = COLUMNS.map((c) => c.example).join(',')
  const csv = '﻿' + headers + '\n' + sample + '\n'
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'plantilla-productos.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ProductosBulkUpload() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [resultado, setResultado] = useState<ResultadoCarga | null>(null)
  const [err, setErr] = useState<string | null>(null)

  function onSelectFile(f: File | null) {
    setErr(null)
    setResultado(null)
    if (!f) {
      setFile(null)
      return
    }
    const ok =
      f.name.toLowerCase().endsWith('.xlsx') ||
      f.name.toLowerCase().endsWith('.xls') ||
      f.name.toLowerCase().endsWith('.csv')
    if (!ok) {
      setErr('Formato no soportado. Usa .xlsx, .xls o .csv.')
      return
    }
    if (f.size > 25 * 1024 * 1024) {
      setErr('El archivo supera 25 MB. Divide la carga en varios bloques.')
      return
    }
    setFile(f)
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(true)
  }

  function onDragLeave() {
    setIsDragging(false)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f) onSelectFile(f)
  }

  async function onUpload() {
    if (!file) return
    setUploading(true)
    setErr(null)
    setResultado(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/productos/bulk-upload', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      })
      const j = await r.json()
      if (!r.ok) {
        setErr(j?.error || `HTTP ${r.status}`)
      } else {
        setResultado(j as ResultadoCarga)
      }
    } catch (e: any) {
      setErr(e?.message || 'Error subiendo archivo')
    } finally {
      setUploading(false)
    }
  }

  function reset() {
    setFile(null)
    setResultado(null)
    setErr(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <>
      <Topbar
        title="Carga masiva de productos"
        subtitle="Sube un Excel con SKUs, precios y stock para actualizar/crear en lote"
        actions={
          <Link
            to="/productos"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </Link>
        }
      />

      <div className="p-6">
        <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-3">
          {/* Columna izquierda: dropzone + acciones */}
          <div className="space-y-6 lg:col-span-2">
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`relative rounded-2xl border-2 border-dashed bg-white p-10 text-center transition-colors ${
                isDragging ? 'border-brand-500 bg-brand-50/40' : 'border-ink-200 hover:border-brand-300'
              }`}
            >
              <div className="mx-auto grid size-14 place-items-center rounded-full bg-brand-50 text-brand-600">
                <UploadCloud className="size-7" />
              </div>
              <h2 className="mt-4 text-base font-semibold text-ink-900">
                Arrastra el archivo aquí
              </h2>
              <p className="mt-1 text-sm text-ink-500">
                O selecciona desde tu equipo (.xlsx, .xls o .csv, máx. 25 MB)
              </p>

              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
              >
                <FileSpreadsheet className="size-4" /> Elegir archivo
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => onSelectFile(e.target.files?.[0] ?? null)}
              />

              {file && (
                <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-ink-200 bg-ink-50 p-3 text-left">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="size-5 text-brand-600" />
                    <div>
                      <div className="text-sm font-semibold text-ink-900">{file.name}</div>
                      <div className="text-xs text-ink-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={reset}
                    className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                    title="Quitar"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={!file || uploading}
                onClick={onUpload}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="size-4 animate-spin" /> : <UploadCloud className="size-4" />}
                {uploading ? 'Subiendo…' : 'Subir y procesar'}
              </button>
              <button
                type="button"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
              >
                <Download className="size-4" /> Descargar plantilla
              </button>
            </div>

            {err && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            {resultado && (
              <div className="space-y-4 rounded-2xl border border-ink-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="size-5 text-emerald-600" />
                  <h3 className="text-sm font-semibold text-ink-900">Resultado de la carga</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Filas procesadas" value={resultado.total_filas} />
                  <Stat label="Creadas" value={resultado.creadas} tone="success" />
                  <Stat label="Actualizadas" value={resultado.actualizadas} tone="info" />
                  <Stat
                    label="Errores"
                    value={resultado.errores.length}
                    tone={resultado.errores.length > 0 ? 'danger' : 'default'}
                  />
                </div>

                {resultado.errores.length > 0 && (
                  <div className="overflow-hidden rounded-xl border border-ink-200">
                    <table className="w-full text-sm">
                      <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
                        <tr>
                          <th className="w-20 px-3 py-2 font-medium">Fila</th>
                          <th className="px-3 py-2 font-medium">Mensaje</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ink-100">
                        {resultado.errores.slice(0, 50).map((e, i) => (
                          <tr key={i} className="hover:bg-ink-50">
                            <td className="px-3 py-2 font-mono text-xs text-ink-700">{e.row}</td>
                            <td className="px-3 py-2 text-ink-700">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {resultado.errores.length > 50 && (
                      <div className="border-t border-ink-100 bg-ink-50 px-3 py-2 text-xs text-ink-500">
                        Mostrando 50 de {resultado.errores.length} errores.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Columna derecha: guía + columnas */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-ink-900">Columnas esperadas</h3>
              <ul className="space-y-2 text-xs">
                {COLUMNS.map((c) => (
                  <li key={c.key} className="flex items-start justify-between gap-3">
                    <span className="font-mono font-semibold text-ink-900">{c.key}</span>
                    <span className="text-right text-ink-500">{c.example}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-5 text-xs text-ink-600">
              <h3 className="mb-2 text-sm font-semibold text-ink-900">Reglas</h3>
              <ul className="list-disc space-y-1 pl-4">
                <li>El SKU es la llave: existente → actualiza, nuevo → crea.</li>
                <li>
                  <span className="font-mono">category_slug</span> y{' '}
                  <span className="font-mono">brand_slug</span> deben existir previamente.
                </li>
                <li>Las filas con error no detienen la carga.</li>
                <li>Recomendado: máx. 2,000 filas por archivo.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4 text-xs text-brand-900">
              <Badge variant="info">Tip</Badge>
              <p className="mt-2">
                Antes de cargar masivamente, prueba con 5-10 filas para validar las columnas.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  )
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: number
  tone?: 'default' | 'success' | 'info' | 'danger'
}) {
  const styles: Record<string, string> = {
    default: 'bg-ink-50 text-ink-900',
    success: 'bg-emerald-50 text-emerald-800',
    info: 'bg-blue-50 text-blue-800',
    danger: 'bg-red-50 text-red-800',
  }
  return (
    <div className={`rounded-xl px-3 py-2 ${styles[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-0.5 text-xl font-extrabold">{value.toLocaleString('es-MX')}</div>
    </div>
  )
}

export default ProductosBulkUpload
